import React, { useState } from 'react';
import vercelLogger from '../utils/vercelRuntimeLogger';
import { Button } from './ui/button';

const LoggingDiagnostic: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const runDiagnostics = async () => {
    setIsRunning(true);
    const results: string[] = [];
    
    // Test 1: Check environment
    const sessionInfo = vercelLogger.getSessionInfo();
    results.push(`✓ Environment: ${sessionInfo.environment}`);
    results.push(`✓ Is Vercel: ${sessionInfo.isVercel}`);
    results.push(`✓ Session ID: ${sessionInfo.sessionId}`);
    
    // Test 2: Test console logging
    console.log('[Diagnostic] Testing console.log - this should appear in Vercel logs');
    console.warn('[Diagnostic] Testing console.warn - this should appear in Vercel logs');
    console.error('[Diagnostic] Testing console.error - this should appear in Vercel logs');
    results.push('✓ Console methods tested - check Vercel Logs tab');
    
    // Test 3: Test logger methods
    vercelLogger.info('Diagnostic', 'INFO_TEST', { test: 'info level logging' });
    vercelLogger.warn('Diagnostic', 'WARN_TEST', { test: 'warning level logging' });
    vercelLogger.error('Diagnostic', 'ERROR_TEST', new Error('Test error'), { test: 'error level logging' });
    results.push('✓ Logger methods tested - check Vercel Logs tab');
    
    // Test 4: Test API tracking
    try {
      await vercelLogger.trackApiCall(
        'Diagnostic',
        'https://api.example.com/test',
        { method: 'GET' },
        async () => {
          // Simulate API call
          return new Response('test', { status: 200 });
        }
      );
      results.push('✓ API tracking tested');
    } catch (error) {
      results.push('✗ API tracking failed: ' + error);
    }
    
    // Test 5: Check buffered logs
    const bufferedLogs = vercelLogger.getBufferedLogs();
    results.push(`✓ Buffered logs count: ${bufferedLogs.length}`);
    
    // Test 6: Export logs
    const exportData = vercelLogger.exportLogs();
    const exportSize = new Blob([exportData]).size;
    results.push(`✓ Export data size: ${exportSize} bytes`);
    
    setTestResults(results);
    setIsRunning(false);
    
    // Final message
    console.log('=== DIAGNOSTIC COMPLETE ===');
    console.log('Check the Vercel Logs tab at: https://vercel.com/[your-account]/[your-project]/logs');
    console.log('Session ID for filtering:', sessionInfo.sessionId);
  };

  const clearDiagnostics = () => {
    setTestResults([]);
    vercelLogger.clearBuffer();
    console.log('[Diagnostic] Cleared log buffer');
  };

  const testError = () => {
    try {
      throw new Error('Test error from diagnostic component');
    } catch (error) {
      vercelLogger.error('Diagnostic', 'TEST_ERROR', error, {
        intentional: true,
        purpose: 'testing error logging',
      });
      console.error('[Diagnostic] Intentional error for testing:', error);
    }
  };

  return (
    <div className="fixed bottom-20 left-4 z-50 bg-white rounded-lg shadow-lg p-4 max-w-md">
      <h3 className="text-lg font-bold mb-2">Logging Diagnostics</h3>
      
      <div className="space-y-2 mb-4">
        <Button
          onClick={runDiagnostics}
          disabled={isRunning}
          className="w-full"
          size="sm"
        >
          {isRunning ? 'Running...' : 'Run Diagnostics'}
        </Button>
        
        <Button
          onClick={testError}
          variant="outline"
          className="w-full bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
          size="sm"
        >
          Test Error Logging
        </Button>
        
        <Button
          onClick={clearDiagnostics}
          variant="outline"
          className="w-full"
          size="sm"
        >
          Clear Results
        </Button>
      </div>
      
      {testResults.length > 0 && (
        <div className="space-y-1 text-sm">
          <div className="font-medium mb-2">Test Results:</div>
          {testResults.map((result, index) => (
            <div key={index} className="text-gray-700">
              {result}
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-4 p-2 bg-blue-50 rounded text-xs">
        <strong>To view logs in Vercel:</strong>
        <ol className="mt-1 ml-4 list-decimal">
          <li>Go to Vercel Dashboard</li>
          <li>Select your project</li>
          <li>Click "Logs" tab</li>
          <li>Look for [Diagnostic] entries</li>
        </ol>
      </div>
      
      <div className="mt-2 text-xs text-gray-500">
        Press Ctrl+Shift+L to view logger info in console
      </div>
    </div>
  );
};

export default LoggingDiagnostic;